import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { Trophy, Check, X, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  PasswordInput,
  PasswordRequirements,
  passwordMeetsRules,
} from "@/components/PasswordInput";
import { toast } from "sonner";

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
  head: () => ({
    meta: [
      { title: "Set new password — Bracket Goblin" },
      { name: "description", content: "Choose a new password for your Bracket Goblin account." },
      { property: "og:title", content: "Set new password — Bracket Goblin" },
      { property: "og:description", content: "Choose a new password for your Bracket Goblin account." },
      { property: "og:url", content: "https://bracketgoblin.com/reset-password" },
    ],
    links: [{ rel: "canonical", href: "https://bracketgoblin.com/reset-password" }],
  }),
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);
  const [checked, setChecked] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (
        event === "PASSWORD_RECOVERY" ||
        event === "SIGNED_IN" ||
        event === "INITIAL_SESSION" ||
        event === "TOKEN_REFRESHED"
      ) {
        if (session) setReady(true);
      }
      setChecked(true);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setReady(true);
      setChecked(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const allRulesPass = passwordMeetsRules(password);
  const passwordsMatch = password.length > 0 && password === confirm;
  const canSubmit = allRulesPass && passwordsMatch && !submitting;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!allRulesPass) {
      toast.error("Password doesn't meet all requirements yet.");
      return;
    }
    if (!passwordsMatch) {
      toast.error("Passwords don't match");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Password updated! Redirecting…");
    setTimeout(() => navigate({ to: "/home" }), 800);
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-2">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-[var(--shadow-neon)]">
            <Trophy className="h-6 w-6" strokeWidth={2.5} />
          </div>
          <h1 className="font-display text-2xl font-black uppercase tracking-tight text-foreground">
            Set your password
          </h1>
        </div>

        {!ready ? (
          !checked ? (
            <div className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
              Validating link…
            </div>
          ) : resent ? (
            <div className="rounded-xl border border-border bg-card p-6 text-center text-sm">
              <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-success/10">
                <Mail className="h-5 w-5 text-success" />
              </div>
              <p className="font-medium text-foreground">Check your email</p>
              <p className="mt-1 text-xs text-muted-foreground">
                A fresh password-reset link is on its way. Click it right away —
                some email apps preview links before you do, which can use up
                your one-time link.
              </p>
            </div>
          ) : (
            <div className="rounded-xl border border-border bg-card p-6 text-sm">
              <p className="font-medium text-foreground">This link is no longer active.</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Password-reset links can only be used once. Some email apps
                (Outlook, Gmail's link scanner, corporate security filters)
                quietly open links before you click them — which uses up the
                link. Send yourself a fresh one and click it right away.
              </p>
              <form
                className="mt-4 space-y-2"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const email = resendEmail.trim();
                  if (!email) {
                    toast.error("Enter your email address.");
                    return;
                  }
                  setResending(true);
                  try {
                    const { error } = await supabase.auth.resetPasswordForEmail(
                      email,
                      { redirectTo: `${window.location.origin}/reset-password` },
                    );
                    if (error) {
                      toast.error(error.message);
                      return;
                    }
                    setResent(true);
                  } catch {
                    toast.error("Something went wrong. Please try again.");
                  } finally {
                    setResending(false);
                  }
                }}
              >
                <Label htmlFor="resend-email" className="text-xs">Your email</Label>
                <Input
                  id="resend-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="border-primary"
                />
                <Button type="submit" className="w-full" disabled={resending}>
                  {resending ? "Sending…" : "Send me a fresh link"}
                </Button>
              </form>
              <div className="mt-3 text-center text-xs">
                <Link to="/" search={{ redirect: undefined }} className="text-muted-foreground hover:text-foreground">
                  Back to sign in
                </Link>
              </div>
            </div>
          )
        ) : (
          <form
            onSubmit={handleSubmit}
            className="space-y-4 rounded-xl border border-border bg-card p-6"
          >
            <div className="space-y-1.5">
              <Label htmlFor="password">New password</Label>
              <PasswordInput
                id="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border-primary"
              />
              <PasswordRequirements value={password} className="mt-2" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm">Confirm password</Label>
              <PasswordInput
                id="confirm"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="border-primary"
              />
              {confirm.length > 0 && (
                <p
                  className={`flex items-center gap-1.5 text-xs ${
                    passwordsMatch ? "text-success" : "text-destructive"
                  }`}
                >
                  {passwordsMatch ? (
                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                  ) : (
                    <X className="w-3.5 h-3.5" />
                  )}
                  {passwordsMatch ? "Passwords match" : "Passwords don't match"}
                </p>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={!canSubmit}>
              {submitting ? "Saving…" : "Set password"}
            </Button>
          </form>
        )}
      </div>
    </main>
  );
}
